import React, { useState, useEffect } from 'react';
import { useContests } from '../../hooks/useAdmin';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { 
  Plus, 
  Trash2, 
  Calendar,
  DollarSign,
  Users,
  HelpCircle,
  X
} from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

const ContestForm = ({ contest, onClose }) => {
  const { createContest, updateContest } = useContests();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    contest_type: 'quiz',
    start_datetime: '',
    end_datetime: '',
    entry_fee: '',
    max_participants: '',
    prize_structure: [
      { position: 1, percentage: 50, minimum_amount: 0 },
      { position: 2, percentage: 30, minimum_amount: 0 },
      { position: 3, percentage: 20, minimum_amount: 0 }
    ],
    questions: [],
    rules: {}
  });

  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: 0,
    time_limit: 30,
    points: 10
  });

  useEffect(() => {
    if (contest) {
      // Format datetime for input fields
      const formatDatetime = (datetime) => {
        const date = new Date(datetime);
        return date.toISOString().slice(0, 16);
      };

      setFormData({
        ...contest,
        start_datetime: formatDatetime(contest.start_datetime),
        end_datetime: formatDatetime(contest.end_datetime),
        entry_fee: contest.entry_fee.toString(),
        max_participants: contest.max_participants?.toString() || ''
      });
    }
  }, [contest]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePrizeStructureChange = (index, field, value) => {
    const updatedPrizeStructure = [...formData.prize_structure];
    updatedPrizeStructure[index] = {
      ...updatedPrizeStructure[index],
      [field]: field === 'position' ? parseInt(value) : parseFloat(value)
    };
    setFormData(prev => ({
      ...prev,
      prize_structure: updatedPrizeStructure
    }));
  };

  const addPrizePosition = () => {
    setFormData(prev => ({
      ...prev,
      prize_structure: [
        ...prev.prize_structure,
        { 
          position: prev.prize_structure.length + 1, 
          percentage: 0, 
          minimum_amount: 0 
        }
      ]
    }));
  };

  const removePrizePosition = (index) => {
    setFormData(prev => ({
      ...prev,
      prize_structure: prev.prize_structure.filter((_, i) => i !== index)
    }));
  };

  const handleQuestionChange = (field, value, optionIndex = null) => {
    if (field === 'options' && optionIndex !== null) {
      const updatedOptions = [...newQuestion.options];
      updatedOptions[optionIndex] = value;
      setNewQuestion(prev => ({
        ...prev,
        options: updatedOptions
      }));
    } else {
      setNewQuestion(prev => ({
        ...prev,
        [field]: field === 'correct_answer' || field === 'time_limit' || field === 'points' 
          ? parseInt(value) 
          : value
      }));
    }
  };

  const addQuestion = () => {
    if (!newQuestion.question_text || newQuestion.options.some(opt => !opt)) {
      setError('Please fill in all question fields and options');
      return;
    }

    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, { ...newQuestion, id: Date.now().toString() }]
    }));

    setNewQuestion({
      question_text: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      time_limit: 30,
      points: 10
    });
  };

  const removeQuestion = (questionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const validateForm = () => {
    if (!formData.title || !formData.description) {
      setError('Title and description are required');
      return false;
    }

    if (!formData.start_datetime || !formData.end_datetime) {
      setError('Start and end dates are required');
      return false;
    }

    if (new Date(formData.start_datetime) >= new Date(formData.end_datetime)) {
      setError('End date must be after start date');
      return false;
    }

    if (!formData.entry_fee || parseFloat(formData.entry_fee) <= 0) {
      setError('Entry fee must be greater than 0');
      return false;
    }

    const totalPercentage = formData.prize_structure.reduce((sum, prize) => sum + prize.percentage, 0);
    if (Math.abs(totalPercentage - 100) > 0.01) {
      setError('Prize structure percentages must total 100%');
      return false;
    }

    if (formData.contest_type === 'quiz' && formData.questions.length === 0) {
      setError('Quiz contests must have at least one question');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        entry_fee: parseFloat(formData.entry_fee),
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: new Date(formData.end_datetime).toISOString()
      };

      if (contest) {
        await updateContest(contest.id, submitData);
      } else {
        await createContest(submitData);
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save contest');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {contest ? 'Edit Contest' : 'Create New Contest'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Contest Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter contest title"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contest_type">Contest Type</Label>
                  <select
                    id="contest_type"
                    name="contest_type"
                    value={formData.contest_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="quiz">Quiz Contest</option>
                    <option value="puzzle">Puzzle Contest</option>
                    <option value="random_draw">Random Draw</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your contest"
                  rows={3}
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Schedule & Participation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Schedule & Participation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_datetime">Start Date & Time *</Label>
                  <Input
                    id="start_datetime"
                    name="start_datetime"
                    type="datetime-local"
                    value={formData.start_datetime}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="end_datetime">End Date & Time *</Label>
                  <Input
                    id="end_datetime"
                    name="end_datetime"
                    type="datetime-local"
                    value={formData.end_datetime}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="entry_fee">Entry Fee (₹) *</Label>
                  <Input
                    id="entry_fee"
                    name="entry_fee"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.entry_fee}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="max_participants">Max Participants (optional)</Label>
                  <Input
                    id="max_participants"
                    name="max_participants"
                    type="number"
                    min="1"
                    value={formData.max_participants}
                    onChange={handleInputChange}
                    placeholder="Leave empty for unlimited"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prize Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <DollarSign className="w-5 h-5 mr-2" />
                Prize Structure
              </CardTitle>
              <CardDescription>
                Define how prizes are distributed among winners
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.prize_structure.map((prize, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    <div>
                      <Label>Position</Label>
                      <Input
                        type="number"
                        min="1"
                        value={prize.position}
                        onChange={(e) => handlePrizeStructureChange(index, 'position', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Percentage (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={prize.percentage}
                        onChange={(e) => handlePrizeStructureChange(index, 'percentage', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Min Amount (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={prize.minimum_amount}
                        onChange={(e) => handlePrizeStructureChange(index, 'minimum_amount', e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removePrizePosition(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                onClick={addPrizePosition}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Prize Position
              </Button>
              
              <div className="text-sm text-gray-600">
                Total percentage: {formData.prize_structure.reduce((sum, prize) => sum + prize.percentage, 0)}%
              </div>
            </CardContent>
          </Card>

          {/* Questions (for quiz contests) */}
          {formData.contest_type === 'quiz' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <HelpCircle className="w-5 h-5 mr-2" />
                  Questions ({formData.questions.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Existing Questions */}
                {formData.questions.map((question, index) => (
                  <div key={question.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium">Question {index + 1}</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeQuestion(question.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{question.question_text}</p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {question.options.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center space-x-2">
                          <Badge variant={optIndex === question.correct_answer ? "default" : "outline"}>
                            {String.fromCharCode(65 + optIndex)}
                          </Badge>
                          <span className="text-sm">{option}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500">
                      Time: {question.time_limit}s | Points: {question.points}
                    </div>
                  </div>
                ))}

                {/* Add New Question */}
                <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg">
                  <h4 className="font-medium mb-4">Add New Question</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Question Text</Label>
                      <Textarea
                        value={newQuestion.question_text}
                        onChange={(e) => handleQuestionChange('question_text', e.target.value)}
                        placeholder="Enter your question"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {newQuestion.options.map((option, index) => (
                        <div key={index}>
                          <Label>Option {String.fromCharCode(65 + index)}</Label>
                          <Input
                            value={option}
                            onChange={(e) => handleQuestionChange('options', e.target.value, index)}
                            placeholder={`Option ${String.fromCharCode(65 + index)}`}
                          />
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Correct Answer</Label>
                        <select
                          value={newQuestion.correct_answer}
                          onChange={(e) => handleQuestionChange('correct_answer', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {newQuestion.options.map((_, index) => (
                            <option key={index} value={index}>
                              Option {String.fromCharCode(65 + index)}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <Label>Time Limit (seconds)</Label>
                        <Input
                          type="number"
                          min="5"
                          max="300"
                          value={newQuestion.time_limit}
                          onChange={(e) => handleQuestionChange('time_limit', e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <Label>Points</Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={newQuestion.points}
                          onChange={(e) => handleQuestionChange('points', e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <Button
                      type="button"
                      onClick={addQuestion}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Question
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{contest ? 'Updating...' : 'Creating...'}</span>
                </div>
              ) : (
                contest ? 'Update Contest' : 'Create Contest'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContestForm;